import { random, shuffled, neighbors, adjacent } from './53-game-primitives.mjs';

export const GEM_COLUMNS = 8;
export const GEM_CELL_COUNT = GEM_COLUMNS * GEM_COLUMNS;

/** Enumerate maximal straight matches. A colour is an integer; null gaps never
 * match. Maximal runs give an unambiguous four/five/cross power policy. */
export function gemRuns(board) {
  const runs = [];
  for (const step of [1, GEM_COLUMNS])
    for (let i = 0; i < GEM_CELL_COUNT; i++) {
      if (board[i] === null) continue;
      const previous = i - step;
      if (
        previous >= 0 &&
        (step === GEM_COLUMNS ||
          Math.floor(previous / GEM_COLUMNS) === Math.floor(i / GEM_COLUMNS)) &&
        board[previous] === board[i]
      )
        continue;
      const cells = [];
      for (
        let j = i;
        j < GEM_CELL_COUNT &&
        (step === GEM_COLUMNS || Math.floor(j / GEM_COLUMNS) === Math.floor(i / GEM_COLUMNS)) &&
        board[j] === board[i];
        j += step
      )
        cells.push(j);
      if (cells.length >= 3) runs.push({ cells, kind: step === 1 ? 'row' : 'col' });
    }
  return runs;
}
/** Unique matched cells, preserving deterministic run order. */
export function gemMatches(board) {
  return [...new Set(gemRuns(board).flatMap((run) => run.cells))];
}
/** Productive swaps include matching colours and an adjacent pair of earned
 * powers, and Spectrum swaps with any ordinary neighbour. Checking alternatives never changes the caller's board or powers. */
export function gemMoves(board, specials = Array(GEM_CELL_COUNT).fill(0)) {
  const result = [];
  for (let i = 0; i < GEM_CELL_COUNT; i++)
    for (const j of neighbors(i, GEM_COLUMNS, GEM_CELL_COUNT)) {
      if (j <= i) continue;
      if (
        (specials[i] && specials[j]) ||
        specials[i] === 'spectrum' ||
        specials[j] === 'spectrum'
      ) {
        result.push([i, j]);
        continue;
      }
      if (board[i] === board[j]) continue;
      const copy = [...board];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      if (gemMatches(copy).length) result.push([i, j]);
    }
  return result;
}

function bannedColours(board, index) {
  const banned = new Set();
  if (index % GEM_COLUMNS >= 2 && board[index - 1] === board[index - 2])
    banned.add(board[index - 1]);
  if (index >= 2 * GEM_COLUMNS && board[index - GEM_COLUMNS] === board[index - 2 * GEM_COLUMNS])
    banned.add(board[index - GEM_COLUMNS]);
  return banned;
}
function stableFill(state, board, colourCount) {
  for (let i = 0; i < GEM_CELL_COUNT; i++) {
    const banned = bannedColours(board, i);
    const choices = Array.from({ length: colourCount }, (_, i) => i).filter((v) => !banned.has(v));
    board[i] = choices[random(state, choices.length)];
  }
}

/** Bounded rejection sampling creates varied openings with several choices. */
export function makeGemBoard(state, colourCount = 4) {
  const board = Array(GEM_CELL_COUNT).fill(null);
  for (let attempt = 0; attempt < 64; attempt++) {
    stableFill(state, board, colourCount);
    if (gemMoves(board).length >= 3) return board;
  }
  // The fallback is a match-free Latin pattern with a proven productive swap.
  const colours = shuffled(
    state,
    Array.from({ length: colourCount }, (_, i) => i),
  );
  const fallback = Array.from(
    { length: GEM_CELL_COUNT },
    (_, i) => (Math.floor(i / GEM_COLUMNS) + 2 * (i % GEM_COLUMNS)) % colourCount,
  );
  fallback[0] = 0;
  fallback[1] = 1;
  fallback[2] = 0;
  fallback[GEM_COLUMNS + 1] = 0;
  return fallback.map((v) => colours[v]);
}

/** Begin with match-free ordinary gems. Powers can only be earned through a
 * later productive transition, never a seed opening or a free selection. */
export function createGemGame(state) {
  state.data = {
    board: makeGemBoard(state),
    specials: Array(GEM_CELL_COUNT).fill(0),
    selected: -1,
    turns: 0,
    combo: 0,
    chain: 0,
    level: 1,
  };
}

function matchComponents(runs) {
  const components = [];
  for (const run of runs) {
    const overlapping = components.filter((c) => run.cells.some((index) => c.cells.has(index)));
    const component = { cells: new Set(run.cells), runs: [run] };
    for (const other of overlapping) {
      other.cells.forEach((index) => component.cells.add(index));
      component.runs.push(...other.runs);
      components.splice(components.indexOf(other), 1);
    }
    components.push(component);
  }
  return components;
}

function earnedPowers(runs, specials, preferred) {
  const earned = [];
  for (const component of matchComponents(runs)) {
    const cross = new Set(component.runs.map((run) => run.kind)).size > 1;
    const longest = component.runs.reduce((best, run) =>
      run.cells.length > best.cells.length ? run : best,
    );
    if (!cross && longest.cells.length < 4) continue;
    const candidates = [...component.cells].filter((index) => !specials[index]);
    if (!candidates.length) continue;
    const index =
      preferred.find((index) => candidates.includes(index)) ??
      candidates[Math.floor(candidates.length / 2)];
    earned.push([index, longest.cells.length >= 5 ? 'spectrum' : 'nova']);
  }
  return earned;
}

function effectCells(index, kind, radius = 1) {
  if (kind === 'row')
    return Array.from(
      { length: GEM_COLUMNS },
      (_, column) => Math.floor(index / GEM_COLUMNS) * GEM_COLUMNS + column,
    );
  if (kind === 'col')
    return Array.from(
      { length: GEM_COLUMNS },
      (_, row) => row * GEM_COLUMNS + (index % GEM_COLUMNS),
    );
  return Array.from({ length: GEM_CELL_COUNT }, (_, i) => i).filter(
    (i) =>
      Math.abs(Math.floor(i / GEM_COLUMNS) - Math.floor(index / GEM_COLUMNS)) <= radius &&
      Math.abs((i % GEM_COLUMNS) - (index % GEM_COLUMNS)) <= radius,
  );
}

/** Resolve each existing power once. Newly created anchors survive this clear,
 * so earning a power and activating it are distinct observable moments. */
function resolveClear(data, runs, triggers, preferred, activation = {}) {
  const earned = activation.combination ? [] : earnedPowers(runs, data.specials, preferred);
  const protectedCells = new Set(earned.map(([index]) => index));
  const clear = new Set(),
    queue = [],
    effects = [],
    effectTargets = [],
    activated = new Set();
  function include(index) {
    if (protectedCells.has(index) || clear.has(index)) return;
    clear.add(index);
    if (data.specials[index]) queue.push(index);
  }
  // Each target-colour gem becomes an emitting Nova for this transition only.
  // The set prevents repeated activation even when explosion footprints overlap.
  if (activation.combination === 'nova-spectrum')
    data.board.forEach((colour, index) => {
      if (
        colour === activation.colour &&
        data.specials[index] !== 'spectrum' &&
        !protectedCells.has(index)
      )
        data.specials[index] = 'nova';
    });
  runs.forEach((run) => run.cells.forEach(include));
  triggers.forEach(include);
  for (let head = 0; head < queue.length; head++) {
    const index = queue[head];
    if (activated.has(index)) continue;
    activated.add(index);
    const kind = data.specials[index];
    let targets;
    if (activation.combination === 'spectrum-spectrum' && triggers.includes(index))
      targets = Array.from({ length: GEM_CELL_COUNT }, (_, i) => i);
    else if (kind === 'spectrum') {
      const colour = triggers.includes(index)
        ? (activation.colour ?? data.board[index])
        : data.board[index];
      targets = data.board.flatMap((value, i) => (value === colour ? [i] : []));
      if (!targets.includes(index)) targets.unshift(index);
    } else
      targets = effectCells(
        index,
        kind,
        activation.combination === 'nova-nova' && triggers.includes(index) ? 2 : 1,
      );
    targets = targets.filter((i) => !protectedCells.has(i));
    effects.push([index, kind]);
    // Eight exact emitter footprints are sufficient for bounded presentation.
    // Full activation identities and clear/scoring sets remain authoritative;
    // repeated64-cell Spectrum footprints must not exhaust the20k state bound.
    if (effectTargets.length < 8) effectTargets.push({ index, kind, targets });
    targets.forEach(include);
  }
  return {
    cells: [...clear],
    effects,
    effectTargets,
    effectTargetsTruncated: effects.length > effectTargets.length,
    earned,
    combination: activation.combination || '',
  };
}

function swapActivation(data, first, second) {
  const a = data.specials[first],
    b = data.specials[second];
  if (a === 'spectrum' && b === 'spectrum')
    return { triggers: [first, second], combination: 'spectrum-spectrum' };
  if ((a === 'spectrum' && b === 'nova') || (a === 'nova' && b === 'spectrum'))
    return {
      triggers: [first, second],
      combination: 'nova-spectrum',
      colour: data.board[a === 'nova' ? first : second],
    };
  if (a === 'nova' && b === 'nova') return { triggers: [first, second], combination: 'nova-nova' };
  if (a === 'spectrum' || b === 'spectrum')
    return { triggers: [first, second], colour: data.board[a === 'spectrum' ? second : first] };
  return { triggers: a && b ? [first, second] : [] };
}

/** Move colour and power identity together. Movement/refill tuples are compact
 * playback evidence; final policy and replay remain independent of animation. */
export function settleGems(state) {
  const d = state.data,
    movements = [],
    refills = [];
  for (let column = 0; column < GEM_COLUMNS; column++) {
    const survivors = [];
    for (let row = 0; row < GEM_COLUMNS; row++) {
      const index = row * GEM_COLUMNS + column;
      if (d.board[index] !== null)
        survivors.push({ index, value: d.board[index], special: d.specials[index] });
    }
    const gap = GEM_COLUMNS - survivors.length;
    for (let row = 0; row < GEM_COLUMNS; row++) {
      const index = row * GEM_COLUMNS + column;
      if (row < gap) {
        d.board[index] = random(state, d.level + 3);
        d.specials[index] = 0;
        refills.push([index, d.board[index]]);
      } else {
        const gem = survivors[row - gap];
        d.board[index] = gem.value;
        d.specials[index] = gem.special;
        if (gem.index !== index) movements.push([gem.index, index]);
      }
    }
  }
  return {
    type: 'gravity',
    movements,
    refills,
    boardAfter: [...d.board],
    specialsAfter: [...d.specials],
  };
}

/** Preserve earned power identities during safety recovery. Try bounded paired
 * match-free paired permutations first. At64 cells, unconstrained random
 * permutations nearly always contain an incidental match; bounded constrained
 * placement preserves the exact inventory while avoiding that recovery bias. A pathological colour distribution can make every ordinary
 * permutation match immediately; the disclosed fallback recolours at the current
 * positions but retains every earned power, rather than erasing player progress. */
export function shuffleGems(state, reason) {
  const d = state.data;
  const pieces = d.board.map((value, i) => ({ value, special: d.specials[i] }));
  for (let attempt = 0; attempt < 64; attempt++) {
    const remaining = [...pieces],
      permutation = [],
      values = [];
    for (let index = 0; index < GEM_CELL_COUNT; index++) {
      const banned = bannedColours(values, index);
      const choices = remaining.flatMap((piece, i) => (banned.has(piece.value) ? [] : [i]));
      if (!choices.length) break;
      const [piece] = remaining.splice(choices[random(state, choices.length)], 1);
      permutation.push(piece);
      values.push(piece.value);
    }
    if (permutation.length !== GEM_CELL_COUNT) continue;
    const board = permutation.map((piece) => piece.value);
    const specials = permutation.map((piece) => piece.special);
    if (gemMatches(board).length || !gemMoves(board, specials).length) continue;
    d.board = board;
    d.specials = specials;
    d.events.push({
      type: 'shuffle',
      reason,
      recoloured: false,
      boardAfter: [...board],
      specialsAfter: [...specials],
    });
    return;
  }
  d.board = makeGemBoard(state, d.level + 3);
  d.events.push({
    type: 'shuffle',
    reason: `${reason}; recoloured safety fallback`,
    recoloured: true,
    boardAfter: [...d.board],
    specialsAfter: [...d.specials],
  });
}

/** Apply a selected adjacent swap with at most eight cascades and64 unique power
 * activations per cascade. Unique cleared cells alone earn clear points. Four
 * in a line or an intersection earns a3x3 Nova; five earns a colour-clearing
 * Spectrum. Spectrum swaps and adjacent powers combine without a colour match. Invalid swaps break
 * the streak but leave the board and all earned powers unchanged. */
export function gemSwap(state, index) {
  const d = state.data;
  if (d.selected < 0) {
    d.selected = index;
    return;
  }
  const first = d.selected;
  d.selected = -1;
  if (first === index) return;
  if (!adjacent(first, index, GEM_COLUMNS)) {
    d.selected = index;
    return;
  }
  const boardBefore = [...d.board],
    specialsBefore = [...d.specials];
  [d.board[first], d.board[index]] = [d.board[index], d.board[first]];
  [d.specials[first], d.specials[index]] = [d.specials[index], d.specials[first]];
  let runs = gemRuns(d.board);
  let activation = swapActivation(d, first, index);
  let triggers = activation.triggers;
  if (!runs.length && !triggers.length) {
    d.board = boardBefore;
    d.specials = specialsBefore;
    d.combo = 0;
    d.chain = 0;
    d.events = [{ type: 'invalid', cells: [first, index], boardBefore, specialsBefore }];
    return;
  }
  d.events.push({ type: 'swap', cells: [first, index], boardBefore, specialsBefore });
  d.turns++;
  d.combo = Math.min(5, d.combo + 1);
  d.chain = 0;
  for (let depth = 1; (runs.length || triggers.length) && depth <= 8; depth++) {
    const clearBoard = [...d.board],
      clearSpecials = [...d.specials];
    const result = resolveClear(d, runs, triggers, depth === 1 ? [index, first] : [], activation);
    const points = result.cells.length * 10 * Math.min(depth, 3) * d.level;
    d.events.push({
      type: 'clear',
      ...result,
      points,
      chain: depth,
      combo: d.combo,
      boardBefore: clearBoard,
      specialsBefore: clearSpecials,
    });
    state.score += points;
    d.chain = depth;
    result.cells.forEach((i) => {
      d.board[i] = null;
      d.specials[i] = 0;
    });
    result.earned.forEach(([i, kind]) => {
      d.specials[i] = kind;
    });
    // Earned snapshots preserve pre-clear colours for coalescence playback. The
    // following gravity event alone owns cleared gaps and final moving identities.
    if (result.earned.length)
      d.events.push({
        type: 'earned',
        pieces: result.earned,
        chain: depth,
        boardAfter: clearBoard,
        specialsAfter: [...d.specials],
      });
    d.events.push(settleGems(state));
    triggers = [];
    activation = {};
    runs = gemRuns(d.board);
  }
  if (runs.length) shuffleGems(state, 'cascade limit');
  else if (!gemMoves(d.board, d.specials).length) shuffleGems(state, 'no swaps');
  const streakPoints = Math.max(0, d.combo - 1) * 25 * d.level;
  if (streakPoints) {
    state.score += streakPoints;
    d.events.push({ type: 'bonus', reason: 'clear streak', points: streakPoints, combo: d.combo });
  }
  const level = d.turns >= 14 ? 3 : d.turns >= 7 ? 2 : 1;
  if (level > d.level) {
    d.level = level;
    const points = (level - 1) * 75;
    state.score += points;
    d.events.push({ type: 'level', level, points });
  }
  state.completed = d.turns >= 20;
}
